import React from 'react';
import { Mail, Phone, MessageCircle, Users, User2, UserCog2 } from 'lucide-react';
import { Button } from './Button';
import { Modal } from './Modal';

interface ConnectWithUsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ConnectWithUsModal: React.FC<ConnectWithUsModalProps> = ({
  isOpen,
  onClose,
}) => {
  const handleEmailClick = (email: string) => {
    window.location.href = `mailto:${email}`;
  };

  const handlePhoneClick = (phone: string) => {
    window.location.href = `tel:${phone}`;
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose}
      title="Connect With Us"
      maxWidth="550px"
    >
      <div className="p-6 animate-fade-in">
        {/* Header section */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 rounded-full bg-primary-light/10 dark:bg-primary-dark/10 flex items-center justify-center mx-auto mb-4">
            <Users className="w-8 h-8 text-primary-light dark:text-primary-dark" />
          </div>
          <h3 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark mb-2">
            We're Here to Help
          </h3>
          <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
            Our support team is ready to assist you with any questions or issues you may have.
          </p>
        </div>

        {/* Guidelines */}
        <div className="mb-6 p-4 rounded-lg border border-border-light dark:border-border-dark bg-background-light/70 dark:bg-background-dark/70">
          <div className="flex items-center mb-2">
            {/* <MessageCircle size={18} className="text-primary-light dark:text-primary-dark mr-2" /> */}
            <h4 className="text-base font-semibold text-text-primary-light dark:text-text-primary-dark">
              Quick Guidelines
            </h4>
          </div>
          <ul className="text-sm text-text-secondary-light dark:text-text-secondary-dark space-y-1">
            <li>• For technical issues or bugs, please email us with detailed steps to reproduce</li>
            <li>• For billing inquiries or subscription changes, include your account email</li>
          </ul>
        </div>

        {/* Email Section */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Mail size={18} className="text-primary-light dark:text-primary-dark" />
            <h4 className="text-base font-semibold text-text-primary-light dark:text-text-primary-dark">
              Email Support
            </h4>
          </div>
          <div className="space-y-3">
            <button
              onClick={() => handleEmailClick('support@mocktrail.app')}
              className="w-full flex items-center justify-between p-4 rounded-lg border border-border-light dark:border-border-dark bg-background-light/50 dark:bg-background-dark/50 hover:bg-background-light dark:hover:bg-background-dark transition-all duration-200 hover:border-primary-light/50 dark:hover:border-primary-dark/50"
            >
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-full bg-primary-light/10 dark:bg-primary-dark/10 flex items-center justify-center mr-3">
                  <Mail size={16} className="text-primary-light dark:text-primary-dark" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark">
                    General Support
                  </p>
                  <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                    support@mocktrail.app
                  </p>
                </div>
              </div>
              <Mail size={16} className="text-text-secondary-light dark:text-text-secondary-dark" />
            </button>

            <button
              onClick={() => handleEmailClick('business@mocktrail.app')}
              className="w-full flex items-center justify-between p-4 rounded-lg border border-border-light dark:border-border-dark bg-background-light/50 dark:bg-background-dark/50 hover:bg-background-light dark:hover:bg-background-dark transition-all duration-200 hover:border-primary-light/50 dark:hover:border-primary-dark/50"
            >
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-full bg-primary-light/10 dark:bg-primary-dark/10 flex items-center justify-center mr-3">
                  <MessageCircle size={16} className="text-primary-light dark:text-primary-dark" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark">
                    Business Inquiries
                  </p>
                  <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                    business@mocktrail.app
                  </p>
                </div>
              </div>
              <Mail size={16} className="text-text-secondary-light dark:text-text-secondary-dark" />
            </button>
          </div>
        </div>

        {/* Phone Section */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Phone size={18} className="text-primary-light dark:text-primary-dark" />
            <h4 className="text-base font-semibold text-text-primary-light dark:text-text-primary-dark">
              Phone Support
            </h4>
          </div>
          <div className="space-y-3">
            <button
              onClick={() => handlePhoneClick('+91-7004297500')}
              className="w-full flex items-center justify-between p-4 rounded-lg border border-border-light dark:border-border-dark bg-background-light/50 dark:bg-background-dark/50 hover:bg-background-light dark:hover:bg-background-dark transition-all duration-200 hover:border-primary-light/50 dark:hover:border-primary-dark/50"
            >
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-full bg-primary-light/10 dark:bg-primary-dark/10 flex items-center justify-center mr-3">
                  <User2 size={16} className="text-primary-light dark:text-primary-dark" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark">
                    Primary Support
                  </p>
                  <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                    +91-70042 97500
                  </p>
                </div>
              </div>
              <Phone size={16} className="text-text-secondary-light dark:text-text-secondary-dark" />
            </button>

            <button
              onClick={() => handlePhoneClick('+91-9877253751')}
              className="w-full flex items-center justify-between p-4 rounded-lg border border-border-light dark:border-border-dark bg-background-light/50 dark:bg-background-dark/50 hover:bg-background-light dark:hover:bg-background-dark transition-all duration-200 hover:border-primary-light/50 dark:hover:border-primary-dark/50"
            >
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-full bg-primary-light/10 dark:bg-primary-dark/10 flex items-center justify-center mr-3">
                  <UserCog2 size={16} className="text-primary-light dark:text-primary-dark" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark">
                    Secondary Support
                  </p>
                  <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                    +91 98772 53751
                  </p>
                </div>
              </div>
              <Phone size={16} className="text-text-secondary-light dark:text-text-secondary-dark" />
            </button>
          </div>
        </div>

        {/* Action Button */}
        <div className="flex justify-end">
          <Button
            variant="primary"
            onClick={onClose}
            className="px-6"
          >
            Thank you!
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ConnectWithUsModal; 